import type {
  ReviewCommentSource,
  ReviewCommentStatus,
} from '../../db/schema/enums.js'

/**
 * What a review comment's status and author mean: who can see it, and who
 * may act on it.
 *
 * Its own leaf module, deliberately: these rules are read by the review
 * service, by the client's feedback reader, by the admin card modal's "N
 * visible to the client" count (LG-504), and — since LG-641 — by the coach's
 * review tab and the fixture lane behind it. The only thing that had been
 * stopping the browser surfaces from importing them was that
 * `defaultReviewService` binds the database client, which no browser bundle
 * may pull in. Nothing here imports anything but types, so every caller shares
 * the one definition instead of keeping a copy in step by comment.
 *
 * `pending` means two different things depending on the author, which is why
 * the predicates below take both fields: a coach comment is pending while it
 * is an unpublished draft, an AI comment while it awaits approval. The two are
 * resolved by different controls — publish, and approve — and neither may
 * resolve the other's.
 */
/**
 * Which statuses the client can see. A total `Record` over
 * `ReviewCommentStatus`, not a set: adding a member to
 * `reviewCommentStatusEnum` is then a compile error here rather than a silent
 * omission that quietly changes what the coach is told the client can see.
 */
export const CLIENT_VISIBLE_BY_COMMENT_STATUS = {
  pending: false,
  approved: true,
  posted: true,
  dismissed: false,
} as const satisfies Record<ReviewCommentStatus, boolean>

/** The visible statuses as a list, for SQL `inArray` reads. */
export const CLIENT_VISIBLE_COMMENT_STATUSES: readonly ReviewCommentStatus[] = (
  Object.keys(CLIENT_VISIBLE_BY_COMMENT_STATUS) as ReviewCommentStatus[]
).filter((status) => CLIENT_VISIBLE_BY_COMMENT_STATUS[status])

export function isClientVisibleCommentStatus(
  status: ReviewCommentStatus,
): boolean {
  return CLIENT_VISIBLE_BY_COMMENT_STATUS[status]
}

/** The two columns every rule below needs: `pending` means nothing alone. */
export interface ReviewCommentAuthorship {
  source: ReviewCommentSource
  status: ReviewCommentStatus
}

/** Written by the coach, not yet published: the client cannot see it. */
export function isCoachDraft(comment: ReviewCommentAuthorship): boolean {
  return comment.source === 'coach' && comment.status === 'pending'
}

/**
 * Published by the coach, i.e. what unpublishing takes back. The service adds
 * one further condition it alone can see — a null `approvedAt`, since
 * `pending` requires one (`ReviewComment_lifecycle_check`) — because the
 * coach's screen does not carry that column.
 */
export function isPublishedCoachComment(
  comment: ReviewCommentAuthorship,
): boolean {
  return comment.source === 'coach' && comment.status === 'posted'
}

/**
 * Whether an edited body can be saved as a revision: what the client can
 * already read, plus the coach's own drafts. A pending *AI* comment is
 * neither — it is resolved by approve/dismiss/rewrite first — so no surface
 * may offer to save one.
 */
export function isSavableComment(comment: ReviewCommentAuthorship): boolean {
  return isClientVisibleCommentStatus(comment.status) || isCoachDraft(comment)
}

/**
 * When the client last opened feedback that is *currently published* — the one
 * fact an unpublish confirmation is allowed to state.
 *
 * A raw `ReviewFeedbackReceipt.lastViewedAt` cannot answer it. That is the
 * review's newest view over all time, so a client who opened an earlier batch
 * and never came back would read as evidence that they have seen comments
 * published since, and the coach would be warned off withdrawing something
 * nobody read (LG-641).
 *
 * Compared against the *earliest* `postedAt` still standing, not the latest:
 * unpublishing takes back every published coach comment at once, so the
 * question is whether the client was ever looking while any of them was
 * visible. Publishing in two batches and viewing between them means they read
 * part of what is about to disappear, which is a warning, not a clean sheet.
 *
 * `null` therefore means "not since this feedback was published", never
 * "never" — the two are only the same when nothing was published before.
 */
export function viewedSincePublication(
  comments: readonly (ReviewCommentAuthorship & { postedAt: Date | null })[],
  lastViewedAt: Date | null,
): Date | null {
  if (lastViewedAt === null) return null
  const publishedAt = comments
    .filter(isPublishedCoachComment)
    .flatMap((comment) => (comment.postedAt === null ? [] : [comment.postedAt]))
  if (publishedAt.length === 0) return null
  const earliest = publishedAt.reduce((a, b) => (a <= b ? a : b))
  return lastViewedAt >= earliest ? lastViewedAt : null
}
